/**
 * categoryOccupancy — which bytes a MessageContent category's members actually
 * claim, as opposed to the extent its outermost members imply.
 *
 * A category's byte range is a bounding box. `computeCategoryByteSize` measures
 * it from the first register to the end of the last, so a category with members
 * at relative bytes 0 and 9 spans ten bytes while occupying two. Comparing two
 * of those bounding boxes answers "could these categories collide?", and the
 * offset matrix has always treated that as "do these categories collide" — which
 * is the same question only for categories that are dense.
 *
 * Real ones are not. Status at 100 spanning ten bytes and Traffic at 105
 * spanning ten overlap on bytes 105-109, and the matrix paints both red; but if
 * Status has no member at relative byte 5..9 and Traffic none at 0..4, nothing
 * of Status is ever written where anything of Traffic is read. The bounding
 * boxes intersect and the registers do not. The operator is told to move an
 * offset that was already correct, and — worse — learns to ignore the red.
 *
 * This module answers the narrower question by placing every member of a
 * category on its absolute byte and intersecting the two sets of bytes. It can
 * only ever excuse an overlap the range test already found: a byte claimed by
 * both categories is still a collision, and a category whose members fill its
 * whole range behaves exactly as it did before.
 *
 * Pure TypeScript. No React, no antd. The span arithmetic is imported from
 * `adapterSizeMath` rather than restated, because an occupancy map that
 * disagrees with the extent it refines is worse than no occupancy map at all.
 */
import {
  parseCompositeKey,
  categoryKeyToMessageContent,
} from "../../../flatbuffers/types/ln2MessageOptions";
import type { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import { baseOffsetOf, widthOf } from "./adapterSizeMath";
import type { CategoryMapping } from "./adapterSizeMath";

/** The mapping fields occupancy needs. A superset of `CategoryMapping` by the
 *  bit position, which `spanOf` has no use for and a collision test does. */
export interface OccupancyMapping extends CategoryMapping {
  bit_offset: number;
}

/**
 * `bit` for a claim on every bit of its byte.
 *
 * A byte-encoded message reads or writes the whole register, so it collides
 * with anything else on that byte whatever bit that other thing wants. So does
 * a floor-expanded bit mask, which spreads one bit per floor across its bytes
 * and leaves none of them free.
 */
export const WHOLE_BYTE = -1;

/** One byte one mapping claims, and the bit it claims inside it. */
export interface OccupiedByte {
  /** Absolute byte, i.e. already offset by the category's start. */
  absByte: number;
  /** Bit claimed inside `absByte`, or `WHOLE_BYTE`. */
  bit: number;
  /** Index into the mappings array this was derived from, so a caller can name
   *  — and paint — the specific message rather than the whole category. */
  rowIndex: number;
  /** The row's LN2 message key, carried for tooltips. */
  ln2MessageKey: string;
}

/** Where one category sits, and what it actually occupies inside that. */
export interface CategoryOccupancy {
  /** Absolute start of the category's bounding box. */
  start: number;
  /** Absolute end of the bounding box, exclusive. */
  end: number;
  /** Every byte claimed by a member, in ascending order of `absByte`. */
  bytes: OccupiedByte[];
}

/** Two members, on two categories, that claim one byte. */
export interface OccupancyCollision {
  a: OccupiedByte;
  b: OccupiedByte;
  absByte: number;
}

/**
 * Members of `mc`, paired with their index in the original array.
 *
 * `selectCategory` in adapterSizeMath applies the same filter and returns only
 * the rows; the index is what lets a collision name a message, so the filter is
 * re-applied here over entries instead of the rows being matched back up by
 * identity — two rows for the same message with the same offsets are equal in
 * every field, and `indexOf` would report both as the first.
 */
function membersOf<T extends OccupancyMapping>(
  mappings: ReadonlyArray<T>,
  mc: MessageContent | number,
): { row: T; index: number }[] {
  const out: { row: T; index: number }[] = [];
  mappings.forEach((row, index) => {
    if (!row.ln2MessageKey || row.is_pair_secondary) return;
    const parsed = parseCompositeKey(row.ln2MessageKey);
    if (!parsed) return;
    if (categoryKeyToMessageContent(parsed.categoryKey) !== mc) return;
    out.push({ row, index });
  });
  return out;
}

/**
 * The bytes category `mc` actually claims when its range begins at `start`.
 *
 * Positions are measured from the category's FIRST register, which is the same
 * origin `spanOf` measures its length from: the matrix cell holds the address of
 * that first register, and every other member sits at a fixed distance from it.
 * Reading a member's stored offset as an absolute address instead would shift
 * the whole category by its own base, which is the bug `spanOf`'s comment
 * describes from the other direction.
 *
 * Returns an empty occupancy for a category with no members. That is not the
 * same as a category that fills its range, and callers must not treat it as
 * "nothing collides" without also checking `end === start`: a caller that has a
 * range but no mappings to explain it knows less than the range does, and the
 * range must win.
 */
export function categoryOccupancy<T extends OccupancyMapping>(
  mappings: ReadonlyArray<T>,
  mc: MessageContent | number,
  floors: number,
  start: number,
): CategoryOccupancy {
  const members = membersOf(mappings, mc);
  if (members.length === 0) return { start, end: start, bytes: [] };

  return occupancyOf(members, floors, start);
}

/**
 * The bytes the members of `mappings` that `include` selects claim when the
 * first of them sits at `start` -- `categoryOccupancy` for a set of rows chosen
 * by some rule other than their category, such as the rows of ONE category that
 * own no column of their own. Row indices stay indices into `mappings`.
 */
export function rowsOccupancy<T extends OccupancyMapping>(
  mappings: ReadonlyArray<T>,
  include: (row: T) => boolean,
  floors: number,
  start: number,
): CategoryOccupancy {
  const members: { row: T; index: number }[] = [];
  mappings.forEach((row, index) => {
    if (!row.ln2MessageKey || row.is_pair_secondary || !include(row)) return;
    members.push({ row, index });
  });
  if (members.length === 0) return { start, end: start, bytes: [] };
  return occupancyOf(members, floors, start);
}

/** Place a selected set of members on absolute bytes, measured from the first
 *  register — the shared body of `categoryOccupancy` and `columnOccupancy`, so
 *  the two cannot place the same row differently. */
function occupancyOf<T extends OccupancyMapping>(
  members: ReadonlyArray<{ row: T; index: number }>,
  floors: number,
  start: number,
): CategoryOccupancy {
  const lo = members.reduce(
    (acc, m) => Math.min(acc, baseOffsetOf(m.row)),
    Number.POSITIVE_INFINITY,
  );

  const bytes: OccupiedByte[] = [];
  let end = start;
  members.forEach(({ row, index }) => {
    const width = widthOf(row, floors);
    const first = start + (baseOffsetOf(row) - lo);
    // A floor-expanded bit mask spreads one bit per floor over `width` bytes, so
    // it claims all of them and every bit in them. A plain bit row claims one
    // bit of one byte. A byte row claims one whole byte.
    const spreadsOverBytes = width > 1;
    for (let w = 0; w < width; w += 1) {
      bytes.push({
        absByte: first + w,
        bit: row.contentType === "bit" && !spreadsOverBytes ? row.bit_offset : WHOLE_BYTE,
        rowIndex: index,
        ln2MessageKey: row.ln2MessageKey,
      });
    }
    if (first + width > end) end = first + width;
  });

  bytes.sort((x, y) => x.absByte - y.absByte);
  return { start, end, bytes };
}

/**
 * The bytes ONE MATRIX COLUMN actually claims, or `undefined` when that cannot
 * be answered safely.
 *
 * A matrix column is either a whole category (`"control"`) or one broken-out
 * message (`"control:normal:22"`), and the selection rule here is the one
 * `deriveColumnDirection` and `directionsInByteRange` already use — so a
 * column's occupancy, its direction and its size are all measured over the same
 * set of rows. A column matched by a different rule than the one that sized it
 * would place registers outside its own range.
 *
 * `undefined` means "do not refine", and it is returned for the two cases where
 * refining could EXCUSE a real overlap:
 *
 *   - no row matches the column, so there is nothing to say what it contains;
 *   - a floor-expanded row is present and the floor count is unknown, which
 *     would measure a mask spanning `ceil(floors / 8)` bytes as one byte and
 *     leave the rest of it looking free.
 *
 * Failing closed is the only safe direction for a collision check: the caller
 * keeps flagging, exactly as it did before occupancy existed.
 */
export function columnOccupancy<T extends OccupancyMapping>(
  mappings: ReadonlyArray<T> | undefined,
  colKey: string,
  floors: number,
  start: number,
): CategoryOccupancy | undefined {
  if (!mappings || mappings.length === 0) return undefined;

  const members: { row: T; index: number }[] = [];
  mappings.forEach((row, index) => {
    if (row.is_pair_secondary) return;
    if (row.ln2MessageKey === colKey || row.ln2MessageKey.split(":")[0] === colKey) {
      members.push({ row, index });
    }
  });
  if (members.length === 0) return undefined;
  if (floors <= 0 && members.some((m) => m.row.floors_based && m.row.contentType === "bit")) {
    return undefined;
  }

  return occupancyOf(members, floors, start);
}

/**
 * The members of two categories that land on one byte.
 *
 * Byte granularity, deliberately. Two categories sharing a byte is a collision
 * whatever bits they want, because a register is read and written whole and the
 * runtime's offset->message map is keyed on the byte; that is the rule the
 * offset matrix has always applied to categories, and narrowing it to bits here
 * would EXCUSE overlaps that are real. Bit granularity belongs to the other
 * check — two messages of the SAME category on one byte, where the operator
 * chose the bits and a shared byte is the normal case.
 *
 * Returns every colliding pair rather than a boolean, so the caller can paint
 * the two messages responsible instead of the two categories they belong to.
 */
/**
 * Whether two claims on ONE byte actually contend for the same wire.
 *
 * A shared byte is not by itself a collision. Eight messages can sit on one
 * byte, one bit each, and never touch -- that is the normal way a bit-mask
 * category is laid out, and reporting it as an overlap flags a design as broken
 * for doing the ordinary thing. What collides is a shared BIT.
 *
 * `WHOLE_BYTE` is the exception on either side. A byte-encoded message reads or
 * writes the whole register, and a floor-expanded bit mask leaves no bit free,
 * so either one contends with anything else on that byte whatever bit the other
 * wants -- which is why the BWH RVU's `Traffic::P` (a byte-valued position) and
 * `Control::SecureCarCall` (three fully-claimed bytes per car) really do fight
 * over byte 131, while two ordinary bit keys sharing a byte do not.
 */
function claimsCollide(a: OccupiedByte, b: OccupiedByte): boolean {
  if (a.absByte !== b.absByte) return false;
  if (a.bit === WHOLE_BYTE || b.bit === WHOLE_BYTE) return true;
  return a.bit === b.bit;
}

export function occupancyCollisions(
  a: CategoryOccupancy,
  b: CategoryOccupancy,
): OccupancyCollision[] {
  if (a.bytes.length === 0 || b.bytes.length === 0) return [];

  const byByte = new Map<number, OccupiedByte[]>();
  a.bytes.forEach((occ) => {
    const arr = byByte.get(occ.absByte);
    if (arr) arr.push(occ);
    else byByte.set(occ.absByte, [occ]);
  });

  const out: OccupancyCollision[] = [];
  b.bytes.forEach((occ) => {
    (byByte.get(occ.absByte) ?? [])
      .filter((other) => claimsCollide(other, occ))
      .forEach((other) => {
        out.push({ a: other, b: occ, absByte: occ.absByte });
      });
  });
  return out;
}

/**
 * Whether two categories whose RANGES overlap actually collide.
 *
 * The range test stays the gate: this is only ever asked about a pair that
 * already failed it, and it can only excuse. A category with no mappings to
 * describe it — an empty occupancy over a non-empty range — is not excused,
 * because the range is then the only evidence there is and refusing to trust it
 * would turn a missing mapping list into a clean bill of health.
 */
export function rangesActuallyCollide(a: CategoryOccupancy, b: CategoryOccupancy): boolean {
  const aOpaque = a.bytes.length === 0 && a.end > a.start;
  const bOpaque = b.bytes.length === 0 && b.end > b.start;
  if (aOpaque || bOpaque) return true;

  return occupancyCollisions(a, b).length > 0;
}
